import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOpenUnsavedChangesModal, useOverlayContext, usePrevious } from 'seagull';
import { ErrorText } from "../../../constants";
import { useStore, useStoreActions } from "../../../contexts";
import { useOnMount } from "../../../hooks/useOnMount";
import { ServerError } from "../../../infra/ErrorResponse";
import { handleRequest } from "../../../infra/utils";
import { relationshipModel } from "../../../models";
import { requestAccountantAccessModel } from "../../../models/request-accountant-access";
import StripeElementsProvider from "../../../providers/StripeElementsProvider";
import { selectors } from "../../../store/state-selectors";
import { ResponsiveProvider } from "../../contexts";
import { BrandingActionsProvider } from "../BrandingActionsContext";
import { COBStep, getIsAllowedToSIgn, isApproveProposalDisabled, PreviewState, Reason } from "../ClientOnboarding.utils";
import { useCobBranding } from "../CobBrandingContext";
import { CobConfigProvider } from "../CobConfigContext";
import { ClientOnboardingContent } from "./ClientOnboardingContent/ClientOnboardingContent";
import { getReachableInnerSteps } from "./ClientOnboardingV2.utils";
import { OptionalServicesContext, PackageSelectionContext, PaymentMethodContext, SignAndApproveContext, useOptionalServicesState, usePackageSelectionState, usePaymentMethodSelection, useTermsOfServiceStatement } from "./hooks";
import { jsx as _jsx } from "react/jsx-runtime";
export const ClientOnboardingInnerV2 = props => {
  var _paymentMethodContext2, _paymentMethodContext5;
  const {
    relationship,
    currentStep,
    previewState,
    disableMobile,
    dismissPreviewModal,
    onPreviewStateChange,
    setCurrentStep,
    refresh
  } = props;
  const [localPreviewState, setLocalPreviewState] = useState(previewState);
  const [signature, setSignature] = useState(null);
  const [approveAgreementStatus, setApproveAgreementStatus] = useState(null);
  const {
    isDirty,
    revertToInitial
  } = useCobBranding();
  const openUnsavedChangesModal = useOpenUnsavedChangesModal();
  const userId = useStore(selectors.user.id);
  const role = useStore(selectors.user.role);
  const {
    api
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  const isPreview = !!previewState;
  const shouldSignStatement = getIsAllowedToSIgn({
    relationship,
    userId,
    role
  });
  const reachableSteps = useMemo(() => getReachableInnerSteps(relationship), [relationship]);
  const getStepIndex = step => reachableSteps.indexOf(step);
  const currentStepIndex = getStepIndex(currentStep);
  const [highestReachedIndex, setHighestReachedIndex] = useState(() => Math.max(currentStepIndex, 0));
  useOnMount(() => {
    if (!isPreview && relationshipModel.hasUserSigned(relationship, userId)) {
      setCurrentStep(COBStep.allSet);
    }
  });
  useEffect(() => {
    if (currentStepIndex > highestReachedIndex) {
      setHighestReachedIndex(currentStepIndex);
    }
  }, [currentStepIndex, highestReachedIndex]);
  const visitedSteps = useMemo(() => reachableSteps.slice(0, highestReachedIndex + 1), [reachableSteps, highestReachedIndex]);
  const prevStep = usePrevious(currentStep);
  const directionRef = useRef(1);
  const hasVisitedCoverPageRef = useRef(false);
  const isCoverPage = currentStep === COBStep.welcome;
  const wasCoverPage = prevStep === COBStep.welcome;
  if (prevStep && prevStep !== currentStep && !isCoverPage && !wasCoverPage) {
    directionRef.current = getStepIndex(currentStep) > getStepIndex(prevStep) ? 1 : -1;
  }
  const handleBack = () => {
    if (currentStepIndex === 0) {
      setCurrentStep(COBStep.welcome);
    } else {
      setCurrentStep(reachableSteps[currentStepIndex - 1]);
    }
  };
  const handleAdvanceToNextStep = useCallback(() => {
    if (currentStepIndex < reachableSteps.length - 1) {
      setCurrentStep(reachableSteps[currentStepIndex + 1]);
    }
  }, [currentStepIndex, reachableSteps, setCurrentStep]);
  const {
    currentStatement,
    isStatementAccepted,
    isNextDisabled,
    handleStatementChange
  } = useTermsOfServiceStatement({
    relationship,
    currentStep,
    shouldSignStatement,
    isPreview
  });
  const {
    hideNextButton,
    packageSelectionContextValue,
    selectedBundleId
  } = usePackageSelectionState({
    relationship,
    currentStep,
    userId,
    handleNext: handleAdvanceToNextStep
  });
  const {
    optionalServicesContextValue,
    selectedOptionalServices
  } = useOptionalServicesState({
    relationship,
    selectedBundleId
  });
  const {
    isPaymentMethodNextDisabled,
    paymentMethodContextValue
  } = usePaymentMethodSelection({
    relationship,
    currentStep,
    isPreview
  });
  const isReviewer = relationshipModel.isReviewer(relationship, userId);
  const hasPaymentMethod = relationship.requirePaymentMethod || relationship.isSample;
  const disabledReason = useMemo(() => {
    var _paymentMethodContext;
    if (isReviewer && currentStep === COBStep.signAndApprove) return Reason.ReviewerApprove;
    if (isPreview) return undefined;
    if (hasPaymentMethod && !(paymentMethodContextValue !== null && paymentMethodContextValue !== void 0 && (_paymentMethodContext = paymentMethodContextValue.selectedPaymentMethod) !== null && _paymentMethodContext !== void 0 && _paymentMethodContext.fundingSourceId)) return Reason.PaymentMethod;
    if (relationship.serviceBundles && !(packageSelectionContextValue !== null && packageSelectionContextValue !== void 0 && packageSelectionContextValue.selectedBundleId)) return Reason.SelectAPackage;
    return undefined;
  }, [isReviewer, isPreview, hasPaymentMethod, currentStep, relationship.serviceBundles, packageSelectionContextValue === null || packageSelectionContextValue === void 0 ? void 0 : packageSelectionContextValue.selectedBundleId, paymentMethodContextValue === null || paymentMethodContextValue === void 0 || (_paymentMethodContext2 = paymentMethodContextValue.selectedPaymentMethod) === null || _paymentMethodContext2 === void 0 ? void 0 : _paymentMethodContext2.fundingSourceId]);
  const baseDisabled = isApproveProposalDisabled(disabledReason, isPreview, relationship, userId, role);
  const handleAgreementSubmission = useCallback(async approvalSignature => {
    var _paymentMethodContext3, _paymentMethodContext4;
    const loaderName = 'lastStep';
    overlayContext.startLoader(loaderName);
    const payload = relationshipModel.isSignatory(relationship, userId) ? {
      fundingSourceId: paymentMethodContextValue === null || paymentMethodContextValue === void 0 || (_paymentMethodContext3 = paymentMethodContextValue.selectedPaymentMethod) === null || _paymentMethodContext3 === void 0 ? void 0 : _paymentMethodContext3.fundingSourceId,
      requestAccountantAccessApproved: requestAccountantAccessModel.wasRequested(relationship.requestAccountantAccess),
      approvalSignature
    } : {
      fundingSourceId: paymentMethodContextValue === null || paymentMethodContextValue === void 0 || (_paymentMethodContext4 = paymentMethodContextValue.selectedPaymentMethod) === null || _paymentMethodContext4 === void 0 ? void 0 : _paymentMethodContext4.fundingSourceId,
      selectedBundleId: packageSelectionContextValue === null || packageSelectionContextValue === void 0 ? void 0 : packageSelectionContextValue.selectedBundleId,
      selectedOptionalServices,
      requestAccountantAccessApproved: requestAccountantAccessModel.wasRequested(relationship.requestAccountantAccess),
      approvalSignature
    };
    const request = relationshipModel.isSignatory(relationship, userId) ? api.relationship.signatoryApproveV2(relationship, payload) : api.relationship.approveV2(relationship, payload);
    const {
      success,
      error
    } = await handleRequest(request);
    overlayContext.stopLoader(loaderName);
    if (!success) {
      if (error.hasServerError(ServerError.UserAlreadySigned)) {
        overlayContext.showSuccessToast('You have already signed the agreement');
      } else {
        const errorText = error.hasServerError(ServerError.MismatchingRelationshipVersion) ? ErrorText.MismatchingRelationshipVersion : error.hasServerError(ServerError.InvalidRelationshipStatus) ? 'The proposal was canceled' : null;
        errorText && overlayContext.showErrorToast(errorText);
        throw error;
      }
    }
  }, [api, relationship, userId, overlayContext, paymentMethodContextValue === null || paymentMethodContextValue === void 0 || (_paymentMethodContext5 = paymentMethodContextValue.selectedPaymentMethod) === null || _paymentMethodContext5 === void 0 ? void 0 : _paymentMethodContext5.fundingSourceId, packageSelectionContextValue === null || packageSelectionContextValue === void 0 ? void 0 : packageSelectionContextValue.selectedBundleId, selectedOptionalServices]);
  const handleApprove = useCallback(async approvalSignature => {
    try {
      setApproveAgreementStatus('loading');
      await handleAgreementSubmission(approvalSignature);
      await refresh();
      setApproveAgreementStatus(null);
      setCurrentStep(COBStep.allSet);
    } catch (_unused) {
      setApproveAgreementStatus('error');
    }
  }, [handleAgreementSubmission, refresh, setCurrentStep]);
  const handleNext = useCallback(() => {
    if (currentStep === COBStep.signAndApprove) {
      if (isPreview) {
        dismissPreviewModal === null || dismissPreviewModal === void 0 || dismissPreviewModal();
        return;
      }
      if (signature) {
        handleApprove(signature);
        return;
      }
    }
    if (currentStepIndex < reachableSteps.length - 1) {
      setCurrentStep(reachableSteps[currentStepIndex + 1]);
    }
  }, [currentStep, currentStepIndex, reachableSteps, setCurrentStep, signature, handleApprove, isPreview, dismissPreviewModal]);
  const isOnSignAndApprove = currentStep === COBStep.signAndApprove;
  const signAndApproveDisabled = isOnSignAndApprove && !isPreview && (!signature || approveAgreementStatus === 'loading');
  const combinedIsNextDisabled = isNextDisabled || isPaymentMethodNextDisabled || signAndApproveDisabled;
  const effectiveHideNextButton = hideNextButton;
  const signAndApproveContextValue = useMemo(() => ({
    signature,
    setSignature,
    baseDisabled
  }), [signature, baseDisabled]);
  const toggleBrandSetup = useCallback(async () => {
    if (localPreviewState === PreviewState.SetupBrand) {
      if (isDirty) {
        const confirmed = await openUnsavedChangesModal({
          testId: 'client-preview-unsaved-popup',
          title: 'Unsaved brand changes'
        });
        if (confirmed) {
          revertToInitial();
          setLocalPreviewState(previewState);
          onPreviewStateChange === null || onPreviewStateChange === void 0 || onPreviewStateChange(previewState);
        }
      } else {
        setLocalPreviewState(previewState);
        onPreviewStateChange === null || onPreviewStateChange === void 0 || onPreviewStateChange(previewState);
      }
    } else {
      setLocalPreviewState(PreviewState.SetupBrand);
      onPreviewStateChange === null || onPreviewStateChange === void 0 || onPreviewStateChange(PreviewState.SetupBrand);
    }
  }, [localPreviewState, previewState, isDirty, revertToInitial, openUnsavedChangesModal, onPreviewStateChange]);
  return /*#__PURE__*/_jsx(ResponsiveProvider, {
    disableMobile: disableMobile,
    children: /*#__PURE__*/_jsx(CobConfigProvider, {
      relationship: relationship,
      previewState: localPreviewState,
      originalPreviewState: previewState,
      isPreviewModal: !!previewState,
      children: /*#__PURE__*/_jsx(BrandingActionsProvider, {
        onToggleBrandSetup: toggleBrandSetup,
        children: /*#__PURE__*/_jsx(StripeElementsProvider, {
          children: /*#__PURE__*/_jsx(PackageSelectionContext.Provider, {
            value: packageSelectionContextValue,
            children: /*#__PURE__*/_jsx(OptionalServicesContext.Provider, {
              value: optionalServicesContextValue,
              children: /*#__PURE__*/_jsx(PaymentMethodContext.Provider, {
                value: paymentMethodContextValue,
                children: /*#__PURE__*/_jsx(SignAndApproveContext.Provider, {
                  value: signAndApproveContextValue,
                  children: /*#__PURE__*/_jsx(ClientOnboardingContent, {
                    relationship: relationship,
                    currentStep: currentStep,
                    previewState: previewState,
                    localPreviewState: localPreviewState,
                    dismissPreviewModal: dismissPreviewModal,
                    onToggleBrandSetup: toggleBrandSetup,
                    showBrandSetupButton: !!previewState,
                    setCurrentStep: setCurrentStep,
                    visitedSteps: visitedSteps,
                    directionRef: directionRef,
                    hasVisitedCoverPageRef: hasVisitedCoverPageRef,
                    handleBack: handleBack,
                    handleNext: handleNext,
                    statement: currentStatement,
                    isStatementAccepted: isStatementAccepted,
                    onStatementChange: handleStatementChange,
                    isNextDisabled: combinedIsNextDisabled,
                    hideNextButton: effectiveHideNextButton,
                    hasSignedSuccess: currentStep === COBStep.allSet
                  })
                })
              })
            })
          })
        })
      })
    })
  });
};